(() => {
  const isEn = document.documentElement.lang === 'en';
  const isKo = document.documentElement.lang === 'ko';
  const configNode = document.querySelector('[data-cart-drawer-config]');
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!configNode || !drawer) return;

  let config;
  try { config = JSON.parse(configNode.textContent); } catch { return; }

  const panel = drawer.querySelector('.cart-drawer__panel');
  const title = drawer.querySelector('[data-cart-drawer-title]');
  const body = drawer.querySelector('[data-cart-drawer-body]');
  const footer = drawer.querySelector('[data-cart-drawer-footer]');
  const summary = drawer.querySelector('[data-cart-drawer-summary]');
  const subtotal = drawer.querySelector('[data-cart-drawer-subtotal]');
  const originalSubtotalRow = drawer.querySelector('[data-cart-original-subtotal-row]');
  const originalSubtotal = drawer.querySelector('[data-cart-original-subtotal]');
  const discountsContainer = drawer.querySelector('[data-cart-discounts]');
  const shippingBar = drawer.querySelector('[data-cart-shipping-bar]');
  const notice = drawer.querySelector('[data-cart-notice]');
  const recommendations = drawer.querySelector('[data-cart-recommendations]');
  const error = drawer.querySelector('[data-cart-drawer-error]');
  let cart = null;
  let lastTrigger = null;
  let inFlight = false;
  let refreshVersion = 0;

  const money = cents => {
    const value = Number(cents || 0);
    const activeCurrency = cart?.currency || config.currency || 'USD';
    const lang = document.documentElement.lang || 'en';
    const localeCode = lang === 'ko' ? 'ko-KR' : (lang === 'ja' ? 'ja-JP' : 'en-US');
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: activeCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100) + ` ${activeCurrency}`;
  };

  const escapeHtml = value => String(value ?? '').replace(/[&<>'"]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));

  const updateCartCount = count => {
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(count); });
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => {
      const label = trigger.dataset.cartLabel || config.cartLabel;
      trigger.setAttribute('aria-label', `${label} (${count})`);
    });
  };

  const setError = message => {
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  };

  const variantLabel = item => item.variant_title && item.variant_title !== 'Default Title'
    ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>`
    : '';

  const discountLabel = value => isEn && /[\u3040-\u30ff\u3400-\u9fff]/.test(value || '') ? 'Discount' : value;

  const itemMarkup = item => {
    const image = item.featured_image?.url || item.image || '';
    const imageMarkup = image
      ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(item.product_title || item.title)}" width="160" height="160" loading="lazy">`
      : '<span class="cart-drawer__image-placeholder" aria-hidden="true"></span>';
    const itemDiscounts = (item.discounts && item.discounts.length > 0)
      ? item.discounts
      : (item.line_level_discount_allocations || []).map(a => ({
          title: a.discount_application?.title || (isKo ? '할인' : (isEn ? "Discount" : '割引')),
          amount: a.amount
        }));
    // Cart original prices describe native discounts, never Variant compare-at.
    // This current-price fallback is used only if the server section is unavailable.
    const originalLinePrice = Number(item.original_line_price ?? item.line_price ?? 0);
    const finalLinePrice = Number(item.final_line_price ?? item.line_price ?? originalLinePrice);
    const hasDiscount = originalLinePrice > finalLinePrice && finalLinePrice >= 0;
    const itemDiscountsMarkup = itemDiscounts.length
      ? `<div class="cart-drawer__item-discounts">${itemDiscounts.map(d => `<span class="cart-drawer__item-discount"><span class="cart-drawer__discount-tag">${isKo ? '할인' : (isEn ? "Discount" : "割引")}</span> ${escapeHtml(discountLabel(d.title))}: -${money(d.amount)}</span>`).join('')}</div>`
      : '';
    return `<article class="cart-drawer__item" data-cart-item-key="${escapeHtml(item.key)}">
      <a class="cart-drawer__image" href="${escapeHtml(item.url)}">${imageMarkup}</a>
      <div class="cart-drawer__item-info">
        <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title || item.title)}</a>
        ${variantLabel(item)}
        <p class="cart-drawer__item-price">${hasDiscount ? `<s data-cart-original-price>${money(originalLinePrice)}</s> ` : ''}<span data-cart-current-price>${money(finalLinePrice)}</span></p>
        ${itemDiscountsMarkup}
        <div class="cart-drawer__item-actions">
          <div class="cart-drawer__quantity" aria-label="${isKo ? '수량' : (isEn ? "Quantity" : "数量")}">
            <button type="button" data-cart-quantity="${item.quantity - 1}" aria-label="${isKo ? '수량 줄이기' : (isEn ? "Decrease quantity" : '数量を減らす')}">−</button>
            <span aria-live="polite">${item.quantity}</span>
            <button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="${isKo ? '수량 늘리기' : (isEn ? "Increase quantity" : '数量を増やす')}">＋</button>
          </div>
          <button class="cart-drawer__remove" type="button" data-cart-remove>${isKo ? '삭제' : (isEn ? "Remove" : "削除")}</button>
        </div>
      </div>
    </article>`;
  };

  const normalizeProduct = product => ({
    id: Number(product.id),
    title: product.title,
    url: product.url,
    available: product.available,
    price: product.price,
    image: product.featured_image?.url || product.featured_image || product.image || null,
    variants: product.variants || []
  });

  const recommendationMarkup = product => {
    const variants = product.variants.filter(variant => variant.available);
    const isSingleVariant = variants.length === 1 && variants[0].title === 'Default Title';
    const image = product.image
      ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" width="220" height="220" loading="lazy">`
      : '<span class="cart-drawer__recommendation-placeholder" aria-hidden="true"></span>';
    const action = isSingleVariant
      ? `<button type="button" data-cart-recommendation-add="${escapeHtml(variants[0].id)}">${isEn ? '＋ Add' : '＋ 追加'}</button>`
      : `<a href="${escapeHtml(product.url)}">${isEn ? "View object" : '商品を見る'}</a>`;
    return `<article class="cart-drawer__recommendation">
      <a class="cart-drawer__recommendation-image" href="${escapeHtml(product.url)}">${image}</a>
      <a class="cart-drawer__recommendation-title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
      <p>${money(product.price)}</p>
      ${action}
    </article>`;
  };

  const renderRecommendations = products => {
    if (!recommendations) return;
    recommendations.replaceChildren();
    recommendations.classList.remove('is-collapsed');
    recommendations.hidden = products.length === 0;
    if (!products.length) return;
    recommendations.innerHTML = `<div class="cart-drawer__recommendations-header">
      <h3>${isEn ? "Recommended objects" : 'おすすめの作品'}</h3>
      <button class="cart-drawer__recommendations-toggle" type="button" data-cart-recommendations-toggle aria-expanded="true" aria-label="${isEn ? "Close recommendations" : 'おすすめの作品を閉じる'}">−</button>
    </div><div class="cart-drawer__recommendation-grid">${products.map(recommendationMarkup).join('')}</div>`;
  };

  const loadRecommendations = async (currentCart, version) => {
    const reference = currentCart.items.find(item => item.product_id);
    if (!reference) return;
    const excludedIds = new Set(currentCart.items.map(item => Number(item.product_id)));
    const selectProducts = products => products.map(normalizeProduct).filter(product => (
      product.available && !excludedIds.has(product.id) && product.id !== Number(reference.product_id)
    )).slice(0, 5);
    let products = [];
    try {
      const rootUrl = isEn ? `${config.rootUrl.replace(/\/$/, '')}/` : config.rootUrl;
      const endpoint = new URL('recommendations/products.json', new URL(rootUrl, window.location.origin)).toString();
      const response = await fetch(`${endpoint}?product_id=${encodeURIComponent(reference.product_id)}&limit=8&intent=related`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (response.ok) products = selectProducts((await response.json()).products || []);
    } catch {}
    if (products.length < 5) {
      const fallback = selectProducts(config.fallbackProducts || []);
      const selectedIds = new Set(products.map(product => product.id));
      products = [...products, ...fallback.filter(product => !selectedIds.has(product.id))].slice(0, 5);
    }
    if (version === refreshVersion) renderRecommendations(products);
  };

  const render = (nextCart, serverItemsHtml = null) => {
    cart = nextCart;
    const isKo = document.documentElement.lang === 'ko';
    const count = cart?.item_count || 0;
    title.textContent = isEn ? `Cart (${count})` : isKo ? `장바구니（${count}）` : `カート（${count}）`;
    updateCartCount(count);
    setError('');
    if (!count) {
      body.innerHTML = isEn ? `<div class="cart-drawer__empty"><p>Your cart is empty.</p><a href="${escapeHtml(config.collectionsUrl)}">Shop all objects <span>→</span></a></div>` : isKo
        ? `<div class="cart-drawer__empty"><p>장바구니가 비어 있습니다.</p><a href="${escapeHtml(config.collectionsUrl)}">작품 보기 <span>→</span></a></div>`
        : `<div class="cart-drawer__empty"><p>カートは空です。</p><a href="${escapeHtml(config.collectionsUrl)}">作品を見る <span>→</span></a></div>`;
      summary.hidden = true;
      if (shippingBar) shippingBar.hidden = true;
      renderRecommendations([]);
      return;
    }
    body.innerHTML = serverItemsHtml ?? `<div class="cart-drawer__items">${cart.items.map(itemMarkup).join('')}</div>`;
    summary.hidden = false;
    renderRecommendations([]);

    // 1. Free Shipping Bar
    const thresholdCents = Number(config.freeShippingThresholdCents || 9900);
    const currentTotal = Number(cart.total_price || 0);
    const isEligibleCountry = Boolean(config.isFreeShippingEligibleCountry);
    const isFreeShipping = Boolean(isEligibleCountry && config.freeShippingEnabled && currentTotal >= thresholdCents);
    if (shippingBar) {
      if (!isEligibleCountry || !config.freeShippingEnabled) {
        shippingBar.hidden = true;
      } else {
        shippingBar.hidden = false;
        if (isFreeShipping) {
          shippingBar.innerHTML = `<p class="cart-drawer__shipping-message is-free" data-cart-shipping-message>${isKo ? '무료 배송이 적용됩니다' : (isEn ? 'Free shipping applied' : '送料無料が適用されます')}</p>`;
        } else {
          const remaining = thresholdCents - currentTotal;
          shippingBar.innerHTML = `<p class="cart-drawer__shipping-message" data-cart-shipping-message>${isKo ? `무료 배송까지 <strong data-cart-shipping-remaining>${money(remaining)}</strong> 남음` : (isEn ? `Add <strong data-cart-shipping-remaining>${money(remaining)}</strong> for free shipping` : `送料無料まであと <strong data-cart-shipping-remaining>${money(remaining)}</strong>`)}</p>`;
        }
      }
    }

    // 2. Summary Discounts
    const discountLines = [];
    if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
      cart.cart_level_discount_applications.forEach(app => {
        discountLines.push({
          title: app.title || (isKo ? '할인' : (isEn ? "Discount" : '割引')),
          amount: Number(app.total_allocated_amount || 0)
        });
      });
    } else {
      cart.items.forEach(item => {
        const lineDiscounts = (item.discounts && item.discounts.length > 0)
          ? item.discounts
          : (item.line_level_discount_allocations || []).map(a => ({ title: a.discount_application?.title || (isKo ? '할인' : (isEn ? "Discount" : '割引')), amount: a.amount }));
        lineDiscounts.forEach(ld => {
          const existing = discountLines.find(d => d.title === ld.title);
          if (existing) {
            existing.amount += Number(ld.amount || 0);
          } else {
            discountLines.push({ title: ld.title, amount: Number(ld.amount || 0) });
          }
        });
      });
    }

    if (discountLines.length === 0 && Number(cart.total_discount || 0) > 0) {
      discountLines.push({ title: (isKo ? '할인' : (isEn ? "Discount" : '割引')), amount: Number(cart.total_discount || 0) });
    }

    const hasDiscount = (Number(cart.total_discount || 0) > 0 || discountLines.length > 0) && Number(cart.original_total_price || 0) > Number(cart.total_price || 0);
    if (originalSubtotalRow) {
      originalSubtotalRow.hidden = !hasDiscount;
      if (originalSubtotal) originalSubtotal.textContent = money(cart.original_total_price);
    }

    if (discountsContainer) {
      if (discountLines.length > 0) {
        if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
          discountsContainer.innerHTML = discountLines.map(line => `
            <div class="cart-drawer__discount-line">
              <span class="cart-drawer__discount-title"><span class="cart-drawer__discount-tag">${isKo ? '할인' : (isEn ? "Discount" : '割引')}</span> ${escapeHtml(discountLabel(line.title))}</span>
              <span class="cart-drawer__discount-amount">-${money(line.amount)}</span>
            </div>
          `).join('');
        } else {
          discountsContainer.innerHTML = `
            <div class="cart-drawer__discount-line">
              <span class="cart-drawer__discount-title"><span class="cart-drawer__discount-tag">${isKo ? '할인' : (isEn ? "Discount" : '割引')}</span> ${isKo ? '할인 적용' : (isEn ? 'Discount applied' : '割引適用')}</span>
              <span class="cart-drawer__discount-amount">-${money(cart.total_discount)}</span>
            </div>
          `;
        }
      } else {
        discountsContainer.innerHTML = '';
      }
    }
    subtotal.textContent = money(cart.total_price);

    // 3. Dynamic notice
    if (notice) {
      if (isEligibleCountry) {
        if (isFreeShipping) {
          notice.innerHTML = isKo
            ? '무료 배송이 적용됩니다 (한국·일본 배송 · $99 이상).<br>관세 및 세금은 결제 시 확인하실 수 있습니다.'
            : (isEn
                ? 'Free shipping applied for orders over $99 USD.<br>Taxes and duties calculated at checkout.'
                : '送料無料が適用されます（日本・韓国宛て・$99以上）。<br>関税等は購入手続き時に確認できます。');
        } else {
          notice.innerHTML = isKo
            ? '배송비는 결제 시 계산됩니다.<br>한국·일본 배송은 $99 이상 주문 시 무료 배송입니다.'
            : (isEn
                ? 'Shipping calculated at checkout.<br>Free shipping on orders over $99 USD to JP & KR.'
                : '送料は購入手続き時に計算されます。<br>日本・韓国宛ては$99以上のご注文で送料無料です。');
        }
      } else {
        notice.textContent = isKo
          ? '배송비 및 세금은 결제 시 계산됩니다.'
          : (isEn
              ? 'Shipping rates and taxes calculated at checkout.'
              : '送料および税金は購入手続き時に計算されます。');
      }
    }
  };

  const matchingServerItems = (items, nextCart) => {
    const rows = Array.from(items?.querySelectorAll('[data-cart-item-key]') || []);
    return items && Number(items.dataset.cartTotal) === Number(nextCart.total_price) &&
      items.dataset.cartCurrency === nextCart.currency && rows.length === nextCart.items.length &&
      rows.every(row => nextCart.items.some(item => item.key === row.dataset.cartItemKey &&
        Number(item.quantity) === Number(row.dataset.cartItemQuantity) &&
        Number(item.original_line_price) === Number(row.dataset.cartItemOriginalLinePrice) &&
        Number(item.final_line_price) === Number(row.dataset.cartItemFinalLinePrice)));
  };

  const refresh = async () => {
    const version = ++refreshVersion;
    const response = await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error('cart-fetch-failed');
    const nextCart = await response.json();
    let serverItemsHtml = null;
    let sectionFailed = false;
    if (nextCart.item_count) {
      try {
        const sectionId = config.cartItemsSectionId || 'cart-drawer-items';
        const sectionUrl = new URL(config.cartUrl, window.location.origin);
        sectionUrl.searchParams.set('sections', sectionId);
        // /cart negotiates Cart JSON for application/json, even with ?sections=.
        const sectionResponse = await fetch(sectionUrl, { headers: { Accept: 'text/html' }, credentials: 'same-origin' });
        if (!sectionResponse.ok) throw new Error('cart-section-fetch-failed');
        const sections = await sectionResponse.json();
        if (typeof sections[sectionId] !== 'string') throw new Error('cart-section-unavailable');
        const sectionDocument = new DOMParser().parseFromString(sections[sectionId], 'text/html');
        const items = sectionDocument.querySelector('[data-cart-rendered-items]');
        if (!matchingServerItems(items, nextCart)) {
          throw new Error('cart-section-snapshot-mismatch');
        }
        serverItemsHtml = items.outerHTML;
      } catch {
        sectionFailed = true;
      }
    }
    // A slower open/refresh must not overwrite a newer quantity/add response.
    if (version !== refreshVersion) return nextCart;
    if (sectionFailed) {
      const existingItems = body.querySelector('[data-cart-rendered-items]');
      if (matchingServerItems(existingItems, nextCart)) serverItemsHtml = existingItems.outerHTML;
    }
    render(nextCart, serverItemsHtml);
    if (sectionFailed) {
      console.warn('Cart section rendering unavailable; ' + (serverItemsHtml ? 'retaining matching server rows.' : 'falling back to standard prices.'));
    }
    if (nextCart.item_count) await loadRecommendations(nextCart, version);
    return nextCart;
  };

  const setBusy = busy => {
    if (busy) ++refreshVersion;
    inFlight = busy;
    drawer.classList.toggle('is-busy', busy);
    drawer.querySelectorAll('[data-cart-quantity], [data-cart-remove]').forEach(control => { control.disabled = busy; });
  };

  const open = async trigger => {
    lastTrigger = trigger || document.activeElement;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(button => button.setAttribute('aria-expanded', 'true'));
    try { if (!inFlight) await refresh(); } catch { setError((isEn ? "Unable to load your cart. Please try again." : 'カートを読み込めませんでした。')); }
    panel.focus();
  };

  const close = () => {
    drawer.hidden = true;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(button => button.setAttribute('aria-expanded', 'false'));
    lastTrigger?.focus?.();
  };

  const change = async (key, quantity) => {
    if (inFlight) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(config.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
      });
      if (!response.ok) throw new Error('cart-change-failed');
      await refresh();
    } catch {
      setError((isEn ? "Unable to update your cart. Please try again." : 'カートを更新できませんでした。'));
    } finally {
      setBusy(false);
    }
  };

  const add = async form => {
    if (inFlight) return;
    const button = form.querySelector('[data-add-to-cart]');
    const originalText = button?.textContent;
    setBusy(true);
    if (button) { button.disabled = true; button.textContent = isEn ? 'Adding…' : '追加中…'; }
    form.querySelector('[data-product-form-error]')?.setAttribute('hidden', '');
    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: new FormData(form)
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.description || 'cart-add-failed');
      }
      await refresh();
      await open(button);
    } catch {
      const productError = form.querySelector('[data-product-form-error]');
      if (productError) {
        productError.textContent = (isEn ? "This object is currently unavailable." : 'この商品は現在ご購入いただけません。');
        productError.hidden = false;
      }
      setError((isEn ? "This object is currently unavailable." : 'この商品は現在ご購入いただけません。'));
    } finally {
      setBusy(false);
      if (button) { button.disabled = false; button.textContent = originalText || (isEn ? "Add to cart" : 'カートに追加'); }
    }
  };

  const addRecommendation = async (variantId, button) => {
    if (inFlight) return;
    const originalText = button.textContent;
    setBusy(true);
    button.disabled = true;
    button.textContent = isEn ? 'Adding…' : '追加中…';
    setError('');
    try {
      const response = await fetch(config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error('cart-add-failed');
      await refresh();
    } catch {
      setError((isEn ? "This object is currently unavailable." : 'この商品は現在ご購入いただけません。'));
      button.disabled = false;
      button.textContent = originalText;
    } finally {
      setBusy(false);
    }
  };

  document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => trigger.addEventListener('click', event => {
    event.preventDefault();
    open(trigger);
  }));
  drawer.querySelectorAll('[data-cart-drawer-close]').forEach(button => button.addEventListener('click', close));
  drawer.addEventListener('click', event => {
    const recommendationsToggle = event.target.closest('[data-cart-recommendations-toggle]');
    const quantityButton = event.target.closest('[data-cart-quantity]');
    const removeButton = event.target.closest('[data-cart-remove]');
    const recommendationButton = event.target.closest('[data-cart-recommendation-add]');
    const item = event.target.closest('[data-cart-item-key]');
    if (recommendationsToggle && recommendations) {
      const collapsed = recommendations.classList.toggle('is-collapsed');
      recommendationsToggle.setAttribute('aria-expanded', String(!collapsed));
      recommendationsToggle.setAttribute('aria-label', collapsed ? (isEn ? "Open recommendations" : 'おすすめの作品を開く') : (isEn ? "Close recommendations" : 'おすすめの作品を閉じる'));
      recommendationsToggle.textContent = collapsed ? '＋' : '−';
    }
    if (quantityButton && item) change(item.dataset.cartItemKey, quantityButton.dataset.cartQuantity);
    if (removeButton && item) change(item.dataset.cartItemKey, 0);
    if (recommendationButton) addRecommendation(recommendationButton.dataset.cartRecommendationAdd, recommendationButton);
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape' && !drawer.hidden) close(); });
  document.addEventListener('submit', event => {
    const form = event.target.closest?.('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    add(form);
  });

  const openFromCartBridge = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') !== 'open') return;
    url.searchParams.delete('cart');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    open();
  };

  openFromCartBridge();
})();
